import type { TechnicianPensieveStats } from '@/hooks/useTechnicianPensieveStats';

export interface PensieveCoachingInsight {
  id: string;
  tone: 'strength' | 'growth' | 'neutral';
  summary: string;
  detail?: string;
}

export function derivePensieveCoaching(
  stats: TechnicianPensieveStats | undefined
): PensieveCoachingInsight[] {
  if (!stats || !stats.hasData) return [];

  const insights: PensieveCoachingInsight[] = [];
  const { strengths, growthAreas, avgDeltaPercent, totalCompletions } = stats;

  // 1) Overall trend insight
  if (avgDeltaPercent != null && totalCompletions >= 10) {
    if (avgDeltaPercent <= -5) {
      insights.push({
        id: 'overall-faster',
        tone: 'strength',
        summary: `Overall you're finishing jobs about ${Math.round(Math.abs(avgDeltaPercent))}% faster than book time.`,
        detail: 'That is a strong indicator that your real-world performance is ahead of the playbook on average.',
      });
    } else if (avgDeltaPercent >= 5) {
      insights.push({
        id: 'overall-slower',
        tone: 'growth',
        summary: `Overall you're taking about ${Math.round(avgDeltaPercent)}% longer than book time.`,
        detail: 'This can be perfectly normal depending on job mix and shop processes, but it also highlights room for process tuning or training.',
      });
    } else {
      insights.push({
        id: 'overall-even',
        tone: 'neutral',
        summary: `Overall you're very close to book time on average.`,
        detail: 'Your real-world performance tracks closely with book, which gives the shop a predictable scheduling baseline.',
      });
    }
  }

  // 2) Strength highlight (use top strength if present)
  const topStrength = strengths?.[0];
  if (topStrength) {
    insights.push({
      id: `strength-${topStrength.id}`,
      tone: 'strength',
      summary: `You are consistently faster on ${topStrength.vehicleLabel} – ${topStrength.jobName}.`,
      detail: `You're about ${Math.round(Math.abs(topStrength.deltaPercent))}% faster than book over ${topStrength.completions} jobs. This is a strong area to lean into for productivity.`,
    });
  }

  // 3) Growth highlight (use top growth area if present)
  const topGrowth = growthAreas?.[0];
  if (topGrowth) {
    insights.push({
      id: `growth-${topGrowth.id}`,
      tone: 'growth',
      summary: `You're running slower than book on ${topGrowth.vehicleLabel} – ${topGrowth.jobName}.`,
      detail: `Currently about ${Math.round(topGrowth.deltaPercent)}% slower than book over ${topGrowth.completions} jobs. Consider extra time, support, or targeted training in this area.`,
    });
  }

  // 4) If no strengths/growthAreas but data exists, fallback neutral
  if (insights.length === 0 && totalCompletions > 0) {
    insights.push({
      id: 'data-building',
      tone: 'neutral',
      summary: 'OptiTime is starting to learn your patterns.',
      detail: 'As you complete more repeat jobs on the same vehicles, OptiBay will surface clearer strengths and growth areas.',
    });
  }

  return insights;
}
